/**
 * Loads CRM people/companies with at least one phone — same API as CrmDialContactsList.
 */

import { authService } from '@/lib/authService';
import type { CrmDialContactRow } from './types';

function primaryPhone(c: CrmDialContactRow): string | null {
  const mobile = c.mobile?.trim();
  const phone = c.phone?.trim();
  return mobile || phone || null;
}

function dedupKey(c: CrmDialContactRow, phone: string): string {
  const norm = phone.replace(/[^\d+]/g, '');
  const id = c.contact_person_id ?? c.contact_id ?? c.id;
  const type = c.crm_record_type || (c.is_person ? 'person' : c.is_company ? 'company' : 'unknown');
  return `${type}:${String(id)}|${norm}`;
}

export async function fetchCrmContactsForDialer(): Promise<CrmDialContactRow[]> {
  try {
    const tokens = authService.getTokens();
    if (!tokens?.access_token) return [];

    const response = await fetch('/api/v1/email-contacts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens.access_token}`,
      },
      body: JSON.stringify({ action: 'getCrmContacts', limit: 500 }),
    });
    if (!response.ok) return [];

    const data = await response.json();
    if (!data.success || !data.data) return [];

    const combined: CrmDialContactRow[] = [
      ...(data.data.contactPeople || []),
      ...(data.data.contacts || []),
    ];
    const withPhone = combined.filter((c) => primaryPhone(c));
    const seen = new Set<string>();
    return withPhone.filter((c) => {
      const phone = primaryPhone(c);
      if (!phone) return false;
      const key = dedupKey(c, phone);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch {
    return [];
  }
}
